import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import styled, { keyframes, css } from "styled-components";
import { theme } from "@upsolve/ui";
import ChatIcon from "./ChatIcon";
import { v4 as uuidV4 } from 'uuid';
import { addYears, format } from "date-fns";
import { UPSOLVE_IDENTIFIER_COOKIE } from "@upsolve/shared";

interface IChatMessage {
  id: number;
  sender: "user" | "ai";
  text: string;
  createdAt: string;
  articleLink?: string;
}

const COOKIE_DOMAIN = process.env.NODE_ENV !== "local" ? ".upsolve.org" : "localhost";

const setCookie = (name: string, value: string) => {
  const twoYearsFromNow = addYears(new Date(), 2);
  const cookieParameters = {
    [name]: value,
    domain: COOKIE_DOMAIN,
    expires: format(twoYearsFromNow, "EEE, dd MMM yyyy HH:mm:ss OOO"),
    path: "/",
  };
  document.cookie = Object.entries(cookieParameters)
    .map(([key, value]) => `${key}=${value}`)
    .join("; ");
};

const getCookie = (name: string) => {
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split("=");
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
};

const refreshCookie = (name: string) => {
  const oldCookieValue = getCookie(name);
  if (oldCookieValue) {
    setCookie(name, oldCookieValue);
  }
};

const findOrCreateAnonymousId = () => {
  const currentAnonymousId = getCookie(UPSOLVE_IDENTIFIER_COOKIE);
  if (currentAnonymousId) {
    refreshCookie(UPSOLVE_IDENTIFIER_COOKIE);
    return currentAnonymousId;
  }

  const anonymousId = uuidV4();
  setCookie(UPSOLVE_IDENTIFIER_COOKIE, anonymousId);
  return anonymousId;
};

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const ChatWindow = styled.div<{ isOpen: boolean; isClosing: boolean }>`
  position: fixed;
  z-index: 5000;
  bottom: 20px;
  right: 20px;
  width: 350px;
  height: 600px;
  background-color: ${theme.colors.white[900]};
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: ${(props) => (props.isOpen || props.isClosing ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  animation: ${(props) =>
    props.isOpen
      ? css`
          ${slideUp} 0.3s ease-out forwards
        `
      : props.isClosing
      ? css`
          ${slideDown} 0.3s ease-in forwards
        `
      : "none"};

  @media (max-height: ${(props) => props.theme.breakpoints[500]}) {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

const ChatHeader = styled.div`
  background-color: ${theme.colors.brand[500]};
  color: ${theme.colors.white[900]};
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.white[900]};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
`;

const ChatMessages = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 15px;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div<{ isUser: boolean }>`
  max-width: 80%;
  margin-bottom: 10px;
  padding: 12px 16px;
  border-radius: 18px;
  background-color: ${(props) => (props.isUser ? theme.colors.brand[500] : "#f4f4f5")};
  color: ${(props) => (props.isUser ? theme.colors.white[900] : theme.colors.black[900])};
  align-self: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  ${(props) => (props.isUser ? "margin-left: auto;" : "margin-right: auto;")}
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
`;

const ChatInputWrapper = styled.div`
  border-top: 1px solid ${theme.colors.gray[300]};
  padding: 15px;
`;

const ChatInput = styled.div`
  display: flex;
  background-color: ${theme.colors.white[900]};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: 20px;
  overflow: hidden;
`;

const ChatInputField = styled.input`
  flex-grow: 1;
  padding: 10px 15px;
  border: none;
  font-size: 14px;
  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button`
  background-color: ${theme.colors.brand[500]};
  color: ${theme.colors.white[900]};
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 0 20px 20px 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
`;

const LoadingDot = styled.span`
  width: 8px;
  height: 8px;
  background-color: ${theme.colors.brand[500]};
  border-radius: 50%;
  display: inline-block;
  margin: 0 4px;
  animation: ${pulse} 1.4s infinite ease-in-out both;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }
  &:nth-child(2) {
    animation-delay: -0.16s;
  }
`;

const ArticleSuggestionsWrapper = styled.div`
  margin-top: 10px;
  border-top: 1px solid ${theme.colors.gray[300]};
  padding-top: 10px;
`;

const SuggestionLink = styled.a`
  color: ${theme.colors.brand[500]};
  text-decoration: none;
  font-weight: bold;
  &:hover {
    text-decoration: underline;
  }
`;

const InvertedContactSupportButton = styled.button`
  background-color: ${theme.colors.gray[700]};
  color: ${theme.colors.white[900]};
  font-size: 12px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  margin: 10px 15px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background-color: ${theme.colors.gray[600]};
  }
`;

async function sendChatMessage(chatId: number | null, message: string, deviceId: string) {
  try {
    // @ts-ignore
    const response = await axios.post(`${UPSOLVE_API_URL}/v1/debtAdvisor/chat/message`, { chatId, query: message, deviceId });
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function fetchChatHistory(deviceId: string) {
  try {
    // @ts-ignore
    const response = await axios.get(`${UPSOLVE_API_URL}/v1/debtAdvisor/chat/${deviceId}/history`);
    return response.data.history;
  } catch (error) {
    console.error("Failed to fetch chat history:", error);
    return [];
  }
}

export const DebtAdvisorChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState<IChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const deviceId = findOrCreateAnonymousId();

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  useEffect(() => {
    const userMessages = chatHistory.filter((msg) => msg.sender === "user");
    setUserMessageCount(userMessages.length);
  }, [chatHistory]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setIsClosing(false);
    if (deviceId) {
      fetchChatHistory(deviceId).then(history => {
        if (history.length > 0) {
          setChatHistory(history);
          setChatId(history[0].debtAdvisorChatId);
        }
      });
    }
  }, [deviceId]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!chatMessage.trim() || isLoading || !deviceId) return;

    setIsLoading(true);
    const newUserMessage: IChatMessage = {
      id: Date.now(),
      sender: "user",
      text: chatMessage,
      createdAt: new Date().toISOString(),
    };
    setChatHistory((prev) => [...prev, newUserMessage]);
    setChatMessage("");

    try {
      const { chatId: newChatId, response } = await sendChatMessage(chatId, chatMessage, deviceId);
      if (!chatId) {
        setChatId(newChatId);
      }

      const newBotMessage: IChatMessage = {
        id: Date.now() + 1,
        sender: "ai",
        text: response.botMessage,
        createdAt: new Date().toISOString(),
        articleLink: JSON.stringify(response.articleSuggestions),
      };
      setChatHistory((prev) => [...prev, newBotMessage]);

      const followUpMessage: IChatMessage = {
        id: Date.now() + 2,
        sender: "ai",
        text: "Do you have any other questions?",
        createdAt: new Date().toISOString(),
      };
      setChatHistory((prev) => [...prev, followUpMessage]);
    } catch (error) {
      const errorMessage: IChatMessage = {
        id: Date.now() + 1,
        sender: "ai",
        text: "Sorry, an error occurred. Please try again.",
        createdAt: new Date().toISOString(),
      };
      setChatHistory((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [chatId, chatMessage, isLoading, deviceId]);

  const renderMessage = (chat: IChatMessage) => {
    const textWithLineBreaks = chat.text.split('\n').map((line, index) => (
      <div key={index}>{line}</div>
    ));
    
    const articles = chat.articleLink ? JSON.parse(chat.articleLink) : [];
    if (!chat.articleLink || articles.length === 0) {
      return textWithLineBreaks;
    }
  
    return (
      <div style={{ marginBottom: '10px' }}>
        {textWithLineBreaks}
        <ArticleSuggestionsWrapper>
          {articles.map((suggestion: { title: string; url: string }, index: number) => {
            const endsWithPunctuation = suggestion.title.match(/[.?!]$/);
            return (
              <div key={index}>
                Learn more in our attorney-written article,{" "}
                <SuggestionLink href={suggestion.url} target="_blank" rel="noopener noreferrer">
                  {suggestion.title}
                </SuggestionLink>
                {!endsWithPunctuation && "."}
              </div>
            );
          })}
        </ArticleSuggestionsWrapper>
      </div>
    );
  };

  // TODO: remove this once we go live
  const onProduction = process.env.NODE_ENV === "production";
  // when on dev.upsolve.org process.env.NODE_ENV is "production" so we need this extra check
  const isDevURL = typeof window !== 'undefined' && window.location.href.includes("dev.");
  if (onProduction && !isDevURL) {
    return null;
  }  

  return (
    <>
      <ChatIcon onClick={handleOpen} />
      <ChatWindow isOpen={isOpen} isClosing={isClosing}>
        <ChatHeader>
          <span>Debt Assistant</span>
          <CloseButton onClick={handleClose}>Close</CloseButton>
        </ChatHeader>
        <ChatMessages>
          {chatHistory.map((chat, index) => (
            <Message
              key={chat.id}
              isUser={chat.sender === "user"}
              ref={index === chatHistory.length - 1 ? lastMessageRef : null}
            >
              {renderMessage(chat)}
            </Message>
          ))}
          {isLoading && (
            <LoadingWrapper>
              <LoadingDot />
              <LoadingDot />
              <LoadingDot />
            </LoadingWrapper>
          )}
          {userMessageCount >= 5 && (
            <InvertedContactSupportButton
              as="a"
              href="https://help.upsolve.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Support
            </InvertedContactSupportButton>
          )}
        </ChatMessages>
        <ChatInputWrapper>
          <ChatInput>
            <ChatInputField
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              placeholder="Type a message..."
            />
            <SendButton onClick={handleSendMessage} disabled={isLoading}>
              Send
            </SendButton>
          </ChatInput>
        </ChatInputWrapper>
      </ChatWindow>
    </>
  );
};
